import { getLocalizedString } from "common/components/localization";
import type { RenderEnvironment } from "common/components/types";
import { SimpleTableRowStyle } from "common/generator/components/simple-table-types";
import { glideViralReferralLink, isEmptyOrUndefined } from "common/Support";
import copyToClipboard from "copy-to-clipboard";
import { ErrorBoundary } from "lib/error-boundary";
import { lazilyLoadedComponent } from "lib/react-lazy-loading";
import * as React from "react";
import AppBanner from "webapp/chrome/views/app-banner/app-banner";
import { getIdealShareUrl } from "webapp/lib/share-utils";
import type { SimpleTableRowDescription } from "webapp/ui/views/simple-table/simple-table-view";
import SimpleTableView from "webapp/ui/views/simple-table/simple-table-view";
import { trackEvent } from "../../../../analytics";
import {
    AppInfoBodyWrapper,
    BodyWrapper,
    Footer,
    FooterWrapper,
    GlideLogo,
    GlideSpan,
    QRCodeWrapper,
    Subtitle,
    Wrapper,
} from "./share-page-style";

const QRCode = lazilyLoadedComponent("qrcode.react", () => import("qrcode.react"));

interface Props {
    readonly title: string | undefined;
    readonly author: string | undefined;
    readonly displayQRCode: boolean;
    readonly renderEnvironment: RenderEnvironment;
    readonly onFeedback: (() => void) | undefined;
    readonly onReport: (() => void) | undefined;
}

interface State {}

function Glide() {
    return (
        <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M14 28C21.732 28 28 21.732 28 14C28 6.26801 21.732 0 14 0C6.26801 0 0 6.26801 0 14C0 21.732 6.26801 28 14 28ZM14.8969 5.03125L5.03125 14.8969H9.87437C12.6482 14.8969 14.8969 12.6482 14.8969 9.87437V5.03125ZM18.1256 13.1031C15.3518 13.1031 13.1031 15.3518 13.1031 18.1256V22.9688L22.9688 13.1031H18.1256Z"
            />
        </svg>
    );
}

export default class SharePage extends React.PureComponent<Props, State> {
    public state: State = { currentPage: "info" };

    private canShowShare = (navigator as any).share !== undefined;

    private onLinkClick = async () => {
        const nav = navigator as any;
        const { appEnvironment } = this.props.renderEnvironment;
        const { appID, appKind } = appEnvironment;
        const url = getIdealShareUrl(appEnvironment);
        if (!this.canShowShare) {
            copyToClipboard(url);
            trackEvent("share", { app_id: appID, method: "copy link" });
        } else {
            try {
                await nav.share({ title: getLocalizedString("shareApp", appKind), url: window.location.href });
                trackEvent("share", { app_id: appID, method: "share sheet" });
            } catch {
                // do nothing
            }
        }
    };

    private onFeedbackClick = () => {
        if (this.props.onFeedback === undefined) return;

        const { appID } = this.props.renderEnvironment.appEnvironment;
        trackEvent("feedback opened", { app_id: appID });
        this.props.onFeedback();
    };

    private onReportClick = () => {
        if (this.props.onReport === undefined) return;

        const { appID } = this.props.renderEnvironment.appEnvironment;
        trackEvent("report opened", { app_id: appID });
        this.props.onReport();
    };

    public render(): React.ReactNode {
        const { renderEnvironment, title, author, displayQRCode, onFeedback, onReport } = this.props;
        const { appEnvironment } = renderEnvironment;
        const { appIcon, appKind } = appEnvironment;
        const url = getIdealShareUrl(appEnvironment);
        const showBranding = !appEnvironment.eminenceFlags.removeBranding;

        let content: React.ReactNode;

        if (displayQRCode) {
            content = (
                <BodyWrapper>
                    <QRCodeWrapper>
                        <ErrorBoundary>
                            <React.Suspense fallback={<div />}>
                                <QRCode
                                    style={{ imageRendering: "pixelated" }}
                                    size={210}
                                    value={url}
                                    fgColor="#000"
                                    bgColor="#fff"
                                />
                            </React.Suspense>
                        </ErrorBoundary>
                    </QRCodeWrapper>
                    <Subtitle>{getLocalizedString("shareThisAppBy", appKind)}</Subtitle>
                </BodyWrapper>
            );
        } else {
            const rows: SimpleTableRowDescription[] = [];
            if (!isEmptyOrUndefined(author)) {
                rows.push({
                    label: getLocalizedString("author", appKind),
                    value: author,
                    style: SimpleTableRowStyle.None,
                });
            }
            if (!appEnvironment.eminenceFlags.canDisableShare || appEnvironment.appFeatures.disableSharing !== true) {
                rows.push({
                    label: getLocalizedString("share", appKind),
                    value: url.replace("https://", ""),
                    style: SimpleTableRowStyle.Link,
                    action: this.onLinkClick,
                });
            }
            if (onFeedback !== undefined) {
                rows.push({
                    label: getLocalizedString("support", appKind),
                    value: getLocalizedString("sendFeedback", appKind),
                    style: SimpleTableRowStyle.PushScreen,
                    action: this.onFeedbackClick,
                });
            }
            if (onReport !== undefined) {
                rows.push({
                    label: getLocalizedString("security", appKind),
                    value: getLocalizedString("reportThisApp", appKind),
                    style: SimpleTableRowStyle.PushScreen,
                    action: this.onReportClick,
                });
            }

            content = (
                <AppInfoBodyWrapper>
                    <AppBanner title={title ?? ""} icon={appIcon} />
                    <SimpleTableView title={undefined} footer={undefined} rows={rows} />

                    {showBranding && (
                        <FooterWrapper href={glideViralReferralLink("about")} target="_blank">
                            <GlideLogo>
                                <Glide />
                            </GlideLogo>
                            <Footer>
                                {getLocalizedString("madeWithPrefix", appKind)}
                                <GlideSpan>glideapps.com</GlideSpan>
                                {getLocalizedString("madeWithPostfix", appKind)}
                            </Footer>
                        </FooterWrapper>
                    )}
                </AppInfoBodyWrapper>
            );
        }

        return <Wrapper>{content}</Wrapper>;
    }
}
